import { CELL_SELECTOR, ScrollPosition } from './scrollPositions';

export type RestoreResult = 'anchor' | 'position' | 'timeout' | 'cancelled';

const TIMEOUT_MS = 3000;
const SETTLE_MS = 1000;
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;

const findCell = (variantId: string) =>
  Array.from(document.querySelectorAll(CELL_SELECTOR)).find(
    cell => cell.getAttribute('data-variant-pk') === variantId
  );

const maxScrollY = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

export const restoreScrollPosition = (
  { y, anchor }: ScrollPosition,
  onDone?: (result: RestoreResult, durationMs: number) => void
) => {
  const start = performance.now();
  let frame = 0;
  let anchorCell: Element | undefined;
  let anchorFoundAt = 0;
  let finished = false;

  const finish = (result: RestoreResult) => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(frame);
    USER_INPUT_EVENTS.forEach(event => window.removeEventListener(event, cancel));
    onDone?.(result, Math.round(performance.now() - start));
  };

  const cancel = () => finish('cancelled');

  const tick = () => {
    const elapsed = performance.now() - start;
    const cell = anchor && findCell(anchor.variantId);

    if (anchor && cell) {
      if (cell !== anchorCell) {
        anchorCell = cell;
        anchorFoundAt = elapsed;
      }

      const offset = cell.getBoundingClientRect().top - anchor.top;
      if (Math.abs(offset) >= 1) window.scrollTo(0, window.scrollY + offset);

      const isLoaded = cell.matches('.VariantCell');
      if (elapsed - anchorFoundAt >= SETTLE_MS && (isLoaded || elapsed >= TIMEOUT_MS)) {
        if (anchor.clicked)
          cell.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true });
        return finish('anchor');
      }
    } else if (!anchor && maxScrollY() >= y) {
      window.scrollTo(0, y);
      return finish('position');
    } else if (elapsed >= TIMEOUT_MS) {
      window.scrollTo(0, y);
      return finish('timeout');
    }

    frame = requestAnimationFrame(tick);
  };

  USER_INPUT_EVENTS.forEach(event => window.addEventListener(event, cancel, { passive: true }));
  tick();

  return cancel;
};
